import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BackdropModal from "../../common/modal/backdropModal";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { selectAmanForm } from "../../../redux/features/amanFormSlice";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import { selectProfileData } from "../../../redux/features/profileSlice";

const QuestionnaireReviewModal = ({ show, onClose }) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;

  const profileData = useSelector(selectProfileData);
  const amanFormData = useSelector(selectAmanForm);
  const basicInfoData = amanFormData?.AMAN_BASIC_INFO || {};

  const formatDateOfBirth = (dateValue) => {
    if (!dateValue) return "-";
    try {
      const dateString = String(dateValue);
      // Handle ISO format: "1996-01-08T00:00:00" or "1996-01-08"
      if (dateString.includes("-")) {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        }
      }
      // Already formatted or other format
      return dateString;
    } catch {
      return String(dateValue);
    }
  };

  const getUserDetails = () => {
    if (isLoggedIn && profileData) {
      const fullName = profileData?.FirstName && profileData?.LastName
        ? `${profileData.FirstName} ${profileData.LastName}`.trim()
        : profileData?.FullName || profileData?.Name || "-";
      
      return {
        name: fullName,
        email: profileData?.Email || "-",
        mobile: profileData?.PhoneNumber || profileData?.MobileNumber || "-",
        dob: formatDateOfBirth(profileData?.DateOfBirth),
      };
    }

    return {
      name: basicInfoData?.fullName || "-",
      email: basicInfoData?.email || "-",
      mobile: basicInfoData?.contactNumber || basicInfoData?.mobile || basicInfoData?.mobileNumber || "-",
      dob: formatDateOfBirth(basicInfoData?.birthDate || basicInfoData?.dateOfBirth) || basicInfoData?.dateOfBirthFormatted || "-",
    };
  };

  const userDetails = getUserDetails();

  const handleGoHome = () => {
    onClose();
    navigate("/");
  };

  if (!show) return null;

  const detailRows = [
    { label: translate("Name", "الاسم"), value: userDetails.name },
    { label: translate("Email Address", "عنوان البريد الإلكتروني"), value: userDetails.email },
    { label: translate("Mobile Number", "رقم الهاتف المحمول"), value: userDetails.mobile },
    { label: translate("Date of Birth", "تاريخ الميلاد"), value: userDetails.dob },
  ];

  return (
    <BackdropModal show={show} onClose={onClose} hideClosebtn>
      <div className={`flex flex-col gap-6 items-start justify-center w-full max-w-[500px] ${isRTL ? "text-right" : "text-left"}`}>
        <div className="flex flex-col gap-2 items-start justify-center w-full">
          <div className="flex items-center justify-between w-full">
            <h2 className={`flex-1 text-[#1A2024] text-2xl font-semibold leading-8 tracking-[-0.24px] ${isRTL ? "text-right" : "text-left"}`}>
              {translate("Review Required", "مراجعة مطلوبة")}
            </h2>
          </div>
          <p className={`text-[#1A2024] text-base font-normal leading-[26px] tracking-[-0.16px] w-full ${isRTL ? "text-right" : "text-left"}`}>
            {translate(
              "Thank you for providing your details. Based on the information submitted, we need to take a closer look at your application.",
              "شكرًا لك على تقديم تفاصيلك. بناءً على المعلومات المقدمة، نحتاج إلى إلقاء نظرة فاحصة على طلبك."
            )}
          </p>
        </div>

        <div className="flex flex-col items-start w-full">
          <div className="bg-white border border-[#DDE2E4] rounded-[10px] w-full overflow-hidden" dir={isRTL ? "rtl" : "ltr"}>
            {detailRows.map((row, index) => (
              <div
                key={index}
                className={`flex items-center w-full ${index < detailRows.length - 1 ? "border-b border-[#DDE2E4]" : ""}`}
              >
                <div className="flex-1 flex items-center px-6 py-2.5 justify-start">
                  <p className="text-[#5B6871] text-sm font-medium leading-6">
                    {row.label}
                  </p>
                </div>
                <div className={`flex-1 flex items-center px-6 py-2.5 justify-start ${isRTL ? "border-r" : "border-l"} border-[#DDE2E4]`}>
                  <p className="text-[#1A2024] text-sm font-medium leading-6">
                    {row.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className={`text-[#1A2024] text-base font-normal leading-[26px] tracking-[-0.16px] w-full ${isRTL ? "text-right" : "text-left"}`}>
          {translate(
            "To ensure we offer you the best possible coverage options, one of our specialized agents will contact you shortly to discuss your specific needs and how we can assist you.",
            "لضمان تقديم أفضل خيارات التغطية الممكنة لك، سيتواصل معك أحد وكلائنا المتخصصين قريبًا لمناقشة احتياجاتك المحددة وكيف يمكننا مساعدتك."
          )}
        </p>

        <div className="flex flex-col items-start w-full">
          <button
            onClick={handleGoHome}
            className="w-full bg-[#55C6EF] rounded-[6px] px-4 py-2 flex items-center justify-center gap-2 overflow-hidden"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" fill="#1A2024"/>
            </svg>
            <span className="text-[#1A2024] text-sm font-semibold leading-6 tracking-[-0.084px]">
              {translate("Return to Home Page", "العودة إلى الصفحة الرئيسية")}
            </span>
          </button>
        </div>
      </div>
    </BackdropModal>
  );
};

export default QuestionnaireReviewModal;
